import React from 'react';
import { SchoolSegment } from '../../types/project.types';
import { segmentDotClass } from '../../utils/segmentColors';

interface SchoolSegmentSelectProps {
  segments: SchoolSegment[];
  value?: string;
  onChange: (segmentId: string | undefined) => void;
  label?: string;
  /** Label for the empty option. For per-child overrides pass e.g. "Same as family". */
  emptyLabel?: string;
  className?: string;
  compact?: boolean;
}

/**
 * Dropdown for choosing a school segment (campus). Renders nothing when the
 * project has no segments, so callers can drop it in unconditionally. Shows a
 * color dot reflecting the current selection.
 */
export const SchoolSegmentSelect: React.FC<SchoolSegmentSelectProps> = ({
  segments,
  value,
  onChange,
  label = 'School / Campus',
  emptyLabel = 'Select a school…',
  className = '',
  compact = false,
}) => {
  if (!segments || segments.length === 0) return null;

  const selected = segments.find(s => s.id === value);

  return (
    <div className={className}>
      {label && (
        <label className={`block ${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-700 mb-1`}>
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${segmentDotClass(selected?.color)}`} />
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          className={`w-full ${compact ? 'px-2 py-1.5 text-sm' : 'px-3 py-2'} border rounded-md focus:ring-indigo-500 focus:border-indigo-500`}
        >
          <option value="">{emptyLabel}</option>
          {segments.map(seg => (
            <option key={seg.id} value={seg.id}>{seg.name}</option>
          ))}
        </select>
      </div>
    </div>
  );
};
